import * as z from "zod"
import { createContext, useContext, ReactNode } from "react"
import { UseFormReturn, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Database } from "@/types/supabase"

// Define the schema for nap form data
export const napFormSchema = z.object({
  childId: z.string().min(1, "Child ID is required"),
  caregiverId: z.string().nullable().optional(),
  start_time: z.date(),
  start_time_input: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format (HH:MM)"),
  end_time: z.date(),
  end_time_input: z.string().regex(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format (HH:MM)"),
  location_id: z.string().optional(),
  environment_id: z.string().optional(),
  onset_method_id: z.string().optional(),
  sleep_latency: z.number().min(0).optional(),
  restfulness_id: z.string().optional(),
  signs_of_sleep_debt: z.boolean().nullable().optional(),
  notes: z.string().optional()
})

// TypeScript type derived from schema
export type NapFormData = z.infer<typeof napFormSchema>

// Database types for strongly typed operations
export type NapSessionRow = Database["public"]["Tables"]["nap_sessions"]["Row"]
export type NapSessionInsert = Database["public"]["Tables"]["nap_sessions"]["Insert"]
export type NapSessionUpdate = Database["public"]["Tables"]["nap_sessions"]["Update"]

const NapFormContext = createContext<UseFormReturn<NapFormData> | null>(null)

export function NapFormProvider({
  children,
  form
}: {
  children: ReactNode
  form: UseFormReturn<NapFormData>
}) {
  return (
    <NapFormContext.Provider value={form}>
      {children}
    </NapFormContext.Provider>
  )
}

export function useNapForm() {
  const context = useContext(NapFormContext)
  if (!context) {
    throw new Error("useNapForm must be used within a NapFormProvider")
  }
  return context
}

export function createNapForm(defaultValues?: Partial<NapFormData>) {
  return useForm<NapFormData>({
    resolver: zodResolver(napFormSchema),
    defaultValues
  })
}
